/* eslint-disable @typescript-eslint/no-explicit-any */
// Personal libs
import { apiPost } from './common/api';
import endpoints from 'src/shared/service/endpoint/endpoints.class';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { AuthResponseDto } from '@data/dto/auth-response.dto';
import { ApiStatusEnum } from '@shared/enum/api-enum';
import JWT from '@shared/class/jwt.class';
import { RoleEnum } from '@shared/enum/role.enum';
import { IUser } from '@shared/interface/model.interface';
import { dbGetUser, dbSetActiveUser, dbSetUser } from '../local/user.database';
import { ApiError } from '@shared/error/api.error';
import { hashRefreshToken } from '@shared/lib/crypto.lib';
import { IAuthRequest } from '@shared/interface/auth-request.interface';
import { IAuthResponse } from '@shared/interface/auth-response.interface';
import { ApiErrorLabel } from '@shared/enum/api-error-label.enum';
import { manageApiError } from './common/api.lib';

//******************************************************************************** POST
/**
 * Forgotten password
 * @returns
 */
export const apiPostForgottenPass = ({
  email,
}: {
  email: string;
}): Promise<IApiResponse<string>> => {
  const payload = {
    email,
  };
  const endpointClass = endpoints.forgottenPass.postForgottenPass;
  return apiPost(endpointClass.getUrl(), payload, {
    publicApi: endpointClass.isPublic(),
  })
    .then((response: IApiResponse<string>) => {
      if (response.returnCode !== 'FORGOT_OK') {
        throw new ApiError(
          response?.returnError ?? '',
          response.returnCode,
          response.rateLimit,
        );
      } else {
        return response;
      }
    })
    .catch((error: ErrorConstructor) =>
      manageApiError(error, 'apiPostForgottenPass'),
    );
};

/**
 * Do authentication
 * @returns
 */
export const apiPostAuth = (
  params: IAuthRequest,
): Promise<IApiResponse<AuthResponseDto, string> | undefined> => {
  const payload = {
    email: params.email?.toLowerCase(),
    password: params.password,
    fingerprint: params.fingerprint
  };
  const endpointClass = endpoints.authentication.postAuth;
  return apiPost(endpointClass.getUrl(), payload, {
    publicApi: endpointClass.isPublic(),
  })
    .then(async (response: IApiResponse<IAuthResponse, string>) => {
      if (
        response.returnCode === 'AUTH_OK' &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        manageAuthResponse(response, params.chkRemember === true);

        return response;
      } else {
        throw new ApiError(
          response?.returnError ?? '',
          response.returnCode,
          response.rateLimit,
        );
      }
    })
    .catch(async (error: ErrorConstructor) => {
      // If auth error, throw Error
      // if (err instanceof ApiError && err.code?.startsWith('AUTH_ERR_')) {
      if (error instanceof ApiError) {
        throw error;

        // Else, manage the error
      } else if (error.toString().includes(ApiErrorLabel.NetworkError)) {
        // Get the user in database from the email to check if the user had successfully authentified at least once
        const user = await dbGetUser({ email: payload.email });

        // Check if user was found
        if (!user) {
          throw new ApiError(
            'AUTH_ERR_OFFLINE',
            'AUTH_ERR_OFFLINE',
          );
        }

        await dbSetActiveUser({
          id: user.id,
          email: user.email,
          isConnected: true,
        });

        // User exists and credentials are OK
        return {
          returnCode: 'AUTH_OK',
          returnStatus: ApiStatusEnum.SUCCESS,
          returnSuccess: {
            jwt_hp: user.jwt_hp,
            refreshToken: user.refreshToken,
            user: {
              ...user,
            },
          },
        };
      }
    });
};

/**
 * Get refresh token
 * @returns
 */
export interface IApiGetTokensParams {
  refreshToken: string;
  uuid: string;
  chkRemember: boolean;
  fingerprint?: string;
}
export const apiPostRefreshTokens = (
  params: IApiGetTokensParams,
): Promise<IApiResponse<IAuthResponse, string>> => {
  const payload = {
    refreshToken: params.refreshToken,
    hash: hashRefreshToken({ uuid: params.uuid }),
    fingerprint: params.fingerprint
  };
  const endpointClass = endpoints.authentication.postAuthRefresh;
  return apiPost(endpointClass.getUrl(), payload, {
    publicApi: endpointClass.isPublic(),
  })
    .then(async (response: IApiResponse<IAuthResponse, string>) => {
      if (
        response.returnCode === 'REFRESH_OK' &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        await manageAuthResponse(response, params.chkRemember === true);
        return response;
      } else {
        throw new ApiError(response?.returnError ?? '', response.returnCode);
      }
    })
    .catch(async (error: ErrorConstructor) =>
      manageApiError(error, 'apiPostRefreshTokens'),
    );
};

export const manageAuthResponse = async (
  response: IApiResponse<IAuthResponse, string>,
  providedChkRemember: boolean,
) => {
  if (response.returnSuccess != null) {
    const jwt = new JWT(response.returnSuccess?.jwt_hp);

    // Create / update the user
    const dbSetUserParams: IUser = {
      ...response.returnSuccess.user,
      isActive: true,
      isActiveLocally: 1,
      isConnected: true,
      refreshToken: response.returnSuccess.refreshToken,
      jwt_hp: response.returnSuccess.jwt_hp,
      roles: jwt.payload?.data.roles.map((r) => RoleEnum[r]),
      chkRemember: providedChkRemember === true,
    };

    await dbSetUser(dbSetUserParams);
    await dbSetActiveUser({
      id: dbSetUserParams.id,
      email: dbSetUserParams.email,
      isConnected: true,
    });
  }
};
